import { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { PayerShell } from '@layouts/PayerShell';
import { RegisterPage } from '@pages/RegisterPage';
import { SignUpOtpPage } from '@pages/SignUpOtpPage';
import { PayerDealView } from '@pages/payer/PayerDealView';
import { loadSignupOtpSession, saveSignupOtpSession } from '@utils/signupOtpSession';

type PayerStep = 'login' | 'otp' | 'view';

export function PayerFlow() {
  const { dealId } = useParams<{ dealId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const { authLoading, isAuthenticated, loginContext } = useAuth();

  const [step, setStep] = useState<PayerStep>('login');
  const [payerPhone, setPayerPhone] = useState('');

  const isValid = useMemo(() => !!dealId && !!token.trim(), [dealId, token]);

  /** Resume payer deal view after refresh when HttpOnly session is still valid. */
  useEffect(() => {
    if (authLoading || !isValid) return;
    if (!isAuthenticated || loginContext !== 'client') return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: resume view step when auth state resolves
    setStep('view');
  }, [authLoading, isAuthenticated, isValid, loginContext]);

  const handlePayerOtpSessionExpired = useCallback(() => {
    setStep('login');
  }, []);

  if (!isValid) {
    return (
      <PayerShell>
        <div className="w-full max-w-md py-12 text-center font-sans text-surface-error-primary">
          <p>{'Invalid or missing payment link. Please use the link from your SMS.'}</p>
        </div>
      </PayerShell>
    );
  }

  const handleBack = () => {
    if (step === 'otp') setStep('login');
  };

  // No back button on the payer deal view screen.
  const showBack = step === 'otp';

  return (
    <PayerShell showBack={showBack} onBack={handleBack}>
      {step === 'login' && (
        <RegisterPage
          contentOnly
          showRightPanel={false}
          authContext="client"
          clientDealId={dealId!}
          clientToken={token}
          onNext={(phone) => {
            saveSignupOtpSession({
              phone,
              authContext: 'client',
              clientDealId: dealId!,
              clientToken: token,
            });
            setPayerPhone(phone);
            setStep('otp');
          }}
        />
      )}
      {step === 'otp' && (
        <SignUpOtpPage
          contentOnly
          showRightPanel={false}
          authContext="client"
          clientDealId={dealId!}
          clientToken={token}
          phoneNumber={payerPhone}
          onSessionExpired={handlePayerOtpSessionExpired}
          onBack={() => setStep('login')}
          onVerify={() => setStep('view')}
          onResendCode={async () => {
            const phone = payerPhone.trim() || loadSignupOtpSession()?.phone?.trim();
            if (phone) {
              const { authRegister } = await import('@services/api');
              await authRegister(phone, { authContext: 'client', dealId: dealId!, token });
            }
          }}
        />
      )}
      {step === 'view' && <PayerDealView dealId={dealId!} token={token} />}
    </PayerShell>
  );
}
